import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner"; // Import loader
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faEnvelope, faLock } from "@fortawesome/free-solid-svg-icons"; // Import icons

const Login = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    setIsLoading(true); // Set loading state to true immediately

    try {
      const response = await axios.post(
        "https://backend.inyumba.co.ke/iNyumba/auth/login",
        formData
      );

      console.log("Login successful response:", response);

      localStorage.setItem("token", response.data.response);
      toast.success("Please check your email for OTP!");

      navigate("/OtpVerification"); // Redirect to OTP verification page
    } catch (error) {
      console.error("Login error:", error);
      if (error.response) {
        // Handle specific API error responses
        if (error.response.status === 401) {
          toast.error("Unauthorized access. Please check your credentials.");
        } else if (error.response.status === 406) {
          toast.error("Please enter the correct email and password.");
        } else if (error.response.status === 400 && error.response.data.response === "Your password is expired please reset password.") {
          toast.error("Your password has expired. Please reset it.");
        } else {
          // Fallback for other 4xx errors with a response message
          toast.error(error.response.data.response || "An error occurred during login. Please try again.");
        }
      } else {
        // Handle network errors or other unexpected issues
        toast.error("Network error or server unreachable. Please try again later.");
      }
    } finally {
      setIsLoading(false); // Always set loading state to false when request is complete
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-md w-full bg-white rounded-xl shadow-2xl p-8 space-y-8 animate-fade-in">
        <div className="text-center">
          {/* Placeholder for your logo */}
          {/* <img className="mx-auto h-16 w-auto mb-4" src="/your-logo.svg" alt="iNyumba Logo" /> */}
          
          <h2 className="text-4xl font-extrabold text-gray-900 mt-6 mb-2">
            Welcome Back! 👋
          </h2>
          <p className="text-gray-600 text-lg">
            Sign in to your account
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Email Input */}
          <div>
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-base transition-colors duration-200"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faLock} className="text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type={passwordVisible ? "text" : "password"}
                autoComplete="current-password"
                required
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-base transition-colors duration-200"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-indigo-600 focus:outline-none transition-colors duration-200"
                title={passwordVisible ? "Hide password" : "Show password"}
              >
                <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-900"
              >
                Remember me
              </label>
            </div>
            <div className="text-sm">
              <a
                href="/forgot-password"
                className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
              >
                Forgot your password?
              </a>
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition-all duration-300 ease-in-out flex items-center justify-center space-x-3 shadow-md
                ${isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-700 transform hover:scale-105"
                }`}
            >
              {isLoading ? (
                <>
                  <Oval
                    height={20}
                    width={20}
                    color="white"
                    ariaLabel="loading-spinner"
                    secondaryColor="rgba(255,255,255,0.4)"
                    strokeWidth={5}
                    strokeWidthSecondary={2}
                  />
                  <span>Logging In...</span>
                </>
              ) : (
                <span>Login</span>
              )}
            </button>
          </div>
        </form>

        {/* Sign Up Link */}
        <div className="text-center">
          <p className="mt-2 text-sm text-gray-600">
            Don't have an account?{" "}
            <a
              href="/registration"
              className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
            >
              Sign up
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;